import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Users } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useChit } from "@/context/ChitContext";
import { useAuth } from "@/context/AuthContext";

// Drawer (bottom sheet) pentru crearea unui grup nou + adăugarea membrilor.
export function AddGroupDrawer({ open, onOpenChange }) {
  const { createGroup } = useChit();
  const { isPremium } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [members, setMembers] = useState([""]);

  const updateMember = (index, value) => {
    const copy = [...members];
    copy[index] = value;
    setMembers(copy);
  };

  const addMemberField = () => {
    // Gratuit: maxim 5 membri într-un grup.
    if (!isPremium && members.length >= 5) {
      toast.error("Grupurile cu peste 5 membri fac parte din Chit Premium.", {
        action: { label: "Vezi Premium", onClick: () => navigate("/premium") },
      });
      return;
    }
    setMembers([...members, ""]);
  };

  const removeMemberField = (index) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const reset = () => {
    setName("");
    setMembers([""]);
  };

  const handleSave = async () => {
    const numeCurate = members.map((m) => m.trim()).filter((m) => m !== "");
    if (name.trim() === "") {
      toast.error("Dă un nume grupului");
      return;
    }
    if (numeCurate.length < 2) {
      toast.error("Adaugă cel puțin 2 membri");
      return;
    }
    try {
      const id = await createGroup(name, numeCurate);
      toast.success("Grup creat!");
      reset();
      onOpenChange(false);
      navigate(`/grup/${id}`);
    } catch {
      toast.error("Eroare la crearea grupului");
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-md mx-auto">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-heading text-2xl font-extrabold flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" strokeWidth={2.5} />
            Grup nou
          </DrawerTitle>
          <DrawerDescription>
            Ex: „Vacanța la mare", „Chirie", „Ieșiri în oraș"
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-5 overflow-y-auto max-h-[60vh]">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Numele grupului
            </label>
            <Input
              data-testid="group-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vacanța la mare"
              className="h-12 rounded-xl text-base"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Membri
            </label>
            <div className="space-y-2">
              {members.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    data-testid={`member-input-${i}`}
                    value={m}
                    onChange={(e) => updateMember(i, e.target.value)}
                    placeholder={`Prieten ${i + 1}`}
                    className="h-12 rounded-xl text-base"
                  />
                  {members.length > 1 && (
                    <Button
                      data-testid={`remove-member-${i}`}
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-slate-400 hover:text-rose-500"
                      onClick={() => removeMemberField(i)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              data-testid="add-member-field-btn"
              variant="ghost"
              onClick={addMemberField}
              className="mt-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl"
            >
              <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />
              Mai adaugă un membru
            </Button>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100">
          <Button
            data-testid="save-group-btn"
            onClick={handleSave}
            className="w-full h-13 py-6 rounded-2xl text-base font-bold bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform"
          >
            Creează grupul
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
